import React, { useState } from 'react';
import clsx from 'clsx';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  wrapperClassName?: string;
  aspect?: string; // e.g. '16/9'
  eager?: boolean; // force eager load for above-the-fold critical images
  showSkeleton?: boolean;
}

/**
 * Lightweight lazy image with optional aspect ratio preservation + skeleton.
 * Uses native loading="lazy" (except when eager) and a simple opacity transition.
 */
export const LazyImage: React.FC<LazyImageProps> = ({
  wrapperClassName,
  aspect,
  eager = false,
  showSkeleton = true,
  className,
  onLoad,
  ...imgProps
}) => {
  const [loaded, setLoaded] = useState(false);
  const aspectClass = aspect ? `aspect-[${aspect}]` : undefined;

  return (
    <div className={clsx('relative overflow-hidden bg-gray-100', aspectClass, wrapperClassName)}>
      {showSkeleton && !loaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-200 to-gray-300" aria-hidden="true" />
      )}
      <img
        {...imgProps}
        className={clsx('w-full h-full object-cover transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0', className)}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
      />
    </div>
  );
};

export default LazyImage;
